import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useMutation } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import StyledCheckbox from '../../components/inputs/styled-checkbox';
import StyledInput from '../../components/inputs/styled-input';
import CookieBar from '../../components/ui-utils/cookie-bar';
import LegalInformation from '../../components/ui-utils/legal-information';
import { Fallback } from '../../components/utils/error-boundary';
import { ACCEPTED_COOKIES_KEY, CookieDAO } from '../../logic/cookies/cookie-dao';
import { GraphqlOperations } from '../../logic/graphql/operations';
import Auth from '../../logic/redux/auth';
import Presentation from '../../logic/redux/presentation';
import { Paths } from '../../navigation/paths';
import config from '../../../config';
import { FormattedMessage, useIntl } from 'react-intl';
import TermsOfUseModalContent from '../../components/ui-utils/terms-of-use';
import { useHistory } from 'react-router-dom';

const UnableToLoginModalContent = (): JSX.Element => {
  const dispatch = useDispatch();

  const handleOkClick = (): void => {
    dispatch(Presentation.actions.setShowModal(undefined));
  };

  return (
    <div className="flex flex-col w-1/1">
      <h2 className="mb-4">
        <FormattedMessage id="loginPage.helpModal.title" />
      </h2>
      <p className="text-12 mb-3">
        <FormattedMessage id="loginPage.helpModal.description" />
      </p>
      <p className="text-12 mb-2">
        <u>
          <FormattedMessage id="loginPage.helpModal.contactLabel" />
        </u>
      </p>
      <ul className="list-disc mb-16 pl-6">
        <li className="text-12 mb-2">
          <FormattedMessage id="loginPage.helpModal.phone" />
        </li>
        <li className="text-12">
          <FormattedMessage id="loginPage.helpModal.email" />
        </li>
      </ul>
      <Button className="self-end" onClick={handleOkClick}>
        <FormattedMessage id="loginPage.helpModal.okButton" />
      </Button>
    </div>
  );
};

const Login = (): JSX.Element => {
  const history = useHistory();
  // MARK: - Resources
  const intl = useIntl();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const firstToken = useSelector(Auth.select.selectFirstToken);
  const authToken = useSelector(Auth.select.selectAuthToken);
  const finishedConfirmation = useSelector(Auth.select.selectFinishedConfirmation);
  const loggedIn = useSelector(Auth.select.selectLoggedIn);
  const loginErrors = useSelector(Auth.select.selectLoginErrors);
  const redirectPath = useSelector(Auth.select.selectLoginRedirectPath);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [secondPassword, setSecondPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState<string>('');
  const changePassword = useMutation(
    GraphqlOperations.mutations.update_user_password.mutationString,
  )[1];
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [newPasswordConfirmationError, setNewPasswordConfirmationError] = useState<string>('');
  const [shouldValidateNewPassword, setShouldValidateNewPassword] = useState<boolean>(false);
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState<boolean>(false);

  const handleLanguageSelect = (language: string): void => {
    dispatch(Auth.actions.setLanguage(language));
  };

  const language = useSelector(Auth.select.selectLanguage) ?? 'hu';
  // MARK: - Lifecycle methods

  useEffect(() => {
    return () => {
      dispatch(Auth.actions.setLoginRedirectPath(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldValidateNewPassword) {
      if (newPassword.length < 8) {
        setNewPasswordError(intl.formatMessage({ id: 'loginPage.errors.passwordLength' }));
      } else if (newPassword === password) {
        setNewPasswordError(intl.formatMessage({ id: 'loginPage.errors.samePassword' }));
      } else {
        setNewPasswordError('');
      }

      if (newPassword !== newPasswordConfirmation) {
        setNewPasswordConfirmationError(
          intl.formatMessage({ id: 'loginPage.errors.passwordsNotMatch' }),
        );
      } else {
        setNewPasswordConfirmationError('');
      }
    }
  }, [intl, password, newPassword, newPasswordConfirmation, shouldValidateNewPassword]);

  // MARK: - Helper methods

  const areNewPasswordFieldsValid = (): boolean => {
    return (
      newPassword.length > 7 && newPassword !== password && newPassword === newPasswordConfirmation
    );
  };

  // MARK: - Event handlers
  const handleLoginFirstClick = (): void => {
    const acceptedCookies = CookieDAO.get(ACCEPTED_COOKIES_KEY);
    if (acceptedCookies) {
      if (acceptedPrivacyPolicy) {
        dispatch(Auth.actions.doLoginFirst(email, password));
      }
    } else {
      addToast(intl.formatMessage({ id: 'loginPage.toastMessages.acceptCookies' }), {
        appearance: 'error',
        autoDismiss: false,
      });
    }
  };

  const handleLoginSecondClick = (): void => {
    dispatch(Auth.actions.doLoginSecond(secondPassword));
  };

  const handleSetNewPasswordClick = (): void => {
    if (areNewPasswordFieldsValid()) {
      const variables = GraphqlOperations.mutations.update_user_password.createVars(
        password,
        newPassword,
      );
      changePassword(variables)
        .then((result) => {
          if (result && !result.error) {
            dispatch(Auth.actions.setUserLoggedIn());
            addToast(intl.formatMessage({ id: 'loginPage.toastMessages.changePasswordSuccess' }), {
              appearance: 'success',
              autoDismiss: true,
            });
          }
          return true;
        })
        .catch((err) => console.log(err));
    } else {
      setShouldValidateNewPassword(true);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.currentTarget.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.currentTarget.value);
  };

  const handleSecondPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSecondPassword(event.currentTarget.value);
  };

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNewPassword(event.currentTarget.value);
  };

  const handleNewPasswordConfirmationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNewPasswordConfirmation(event.currentTarget.value);
  };

  const handleHelpClick = (): void => {
    dispatch(Presentation.actions.setShowModal(<UnableToLoginModalContent />));
  };

  // MARK: - Misc
  if (loggedIn && redirectPath) {
    return <Redirect to={redirectPath} />;
  } else if (loggedIn) {
    return <Redirect to={Paths.dashboard} />;
  }

  // MARK: - UI Functions

  const firstLoginStepContent = (
    <>
      <p className="login-instructions">
        <FormattedMessage id="loginPage.title" />
      </p>
      <StyledInput
        id="email"
        key="email"
        onChange={handleEmailChange}
        placeholder={intl.formatMessage({ id: 'loginPage.loginForm.emailLabel' })}
        blurred
        onEnterPress={handleLoginFirstClick}
        type="email"
        autoComplete="username"
        autoFocus
      />
      <StyledInput
        id="password"
        key="password"
        onChange={handlePasswordChange}
        placeholder={intl.formatMessage({ id: 'loginPage.loginForm.passwordLabel' })}
        type="password"
        error={loginErrors['first'] && intl.formatMessage({ id: loginErrors['first'] })}
        blurred
        onEnterPress={handleLoginFirstClick}
        autoComplete="current-password"
      />
      <span className="text-12 self-end mb-6 underline cursor-pointer" onClick={handleHelpClick}>
        <FormattedMessage id="loginPage.loginForm.helpLabel" />
      </span>
      <Button className="mb-5" inactive={!acceptedPrivacyPolicy} onClick={handleLoginFirstClick}>
        <FormattedMessage id="loginPage.loginForm.loginButton" />
      </Button>
      <a
        className={'text-sm mb-5'}
        onClick={() => handleLanguageSelect(language === 'hu' ? 'en' : 'hu')}
      >
        <FormattedMessage id={`common.languageSelect.${language === 'hu' ? 'en' : 'hu'}`} />
      </a>

      <StyledCheckbox
        onChange={() => {
          setAcceptedPrivacyPolicy(!acceptedPrivacyPolicy);
        }}
        checked={acceptedPrivacyPolicy}
      >
        <FormattedMessage id="loginPage.loginForm.privacyPolicyLabel" />
        :&nbsp;
        <a
          className="underline font-bold"
          href="https://ertektar.hu/adatvedelmi-tajekoztato-a-weboldalhoz/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="loginPage.loginForm.privacyPolicyLink" />
        </a>
      </StyledCheckbox>
    </>
  );

  const secondLoginStepContent = (
    <>
      <h2 className="mb-4">
        <FormattedMessage id="loginPage.smsSignForm.title" />
      </h2>

      <p className="login-instructions">
        <FormattedMessage id="loginPage.smsSignForm.description" />
      </p>
      <StyledInput
        id="confirmation-password"
        key="confirmation-password"
        onChange={handleSecondPasswordChange}
        placeholder={intl.formatMessage({ id: 'loginPage.smsSignForm.smsCodeLabel' })}
        type="text"
        autoComplete="one-time-code"
        inputMode="numeric"
        blurred
        error={loginErrors['second'] && intl.formatMessage({ id: loginErrors['second'] })}
        onEnterPress={handleLoginSecondClick}
        autoFocus
      />
      <span
        onClick={handleLoginFirstClick}
        className="text-12 mb-8 self-end underline cursor-pointer"
      >
        <FormattedMessage id="loginPage.smsSignForm.newCodeButton" />
      </span>
      <div className="flex flex-row">
        <Button
          className="mr-8"
          onClick={() => dispatch(Auth.actions.setFirstToken(undefined))}
          secondary
        >
          <FormattedMessage id="loginPage.smsSignForm.cancelButton" />
        </Button>
        <Button inactive={!secondPassword} onClick={handleLoginSecondClick}>
          <FormattedMessage id="loginPage.smsSignForm.confirmButton" />
        </Button>
      </div>
    </>
  );

  const passwordChangeStepContent = (
    <>
      <h2 className="mb-8">
        <FormattedMessage id="loginPage.newPasswordForm.title" />
      </h2>
      <p className="login-instructions">
        <FormattedMessage id="loginPage.newPasswordForm.description" />
      </p>
      <StyledInput
        id="email"
        key="email"
        value={email}
        disabled
        placeholder={intl.formatMessage({ id: 'loginPage.newPasswordForm.emailLabel' })}
        blurred
        type="email"
        style={{ display: 'none' }}
      />
      <StyledInput
        key="new-password"
        id="new-password"
        onChange={handleNewPasswordChange}
        placeholder={intl.formatMessage({ id: 'loginPage.newPasswordForm.newPasswordLabel' })}
        type="password"
        error={newPasswordError}
        autoComplete="new-password"
        blurred
        onEnterPress={handleSetNewPasswordClick}
        autoFocus
      />
      <StyledInput
        key="new-password-confirmation"
        id="new-password-confirmation"
        onChange={handleNewPasswordConfirmationChange}
        placeholder={intl.formatMessage({
          id: 'loginPage.newPasswordForm.passwordConfirmLabel',
        })}
        type="password"
        autoComplete="new-password"
        error={newPasswordConfirmationError}
        blurred
        onEnterPress={handleSetNewPasswordClick}
      />
      <Button className="mt-4" onClick={handleSetNewPasswordClick}>
        <FormattedMessage id="loginPage.newPasswordForm.changePasswordButton" />
      </Button>
    </>
  );

  const termsOfUseStepContent = (
    <></>
    /*<Modal
      title="Munkavállaló adatvédelmi tájékoztató"
      visible={
        this.state.file !== '' || (this.props.accepted != null && this.props.accepted == false)
          ? true
          : false
      }
      onOk={this.handleOk}
      closable={false}
      footer={[
        <Button
          loading={this.props.loading}
          disabled={this.state.file === '' && !this.state.checked}
          key="back"
          onClick={this.handleOk}
        >
          Ok
        </Button>,
      ]}
    >
      {
        <Document
          className={'embed-pdf'}
          file={
            this.state.file !== ''
              ? `${apiUrl}api/gdpr/file/${this.state.file}`
              : `${apiUrl}api/gdpr/file/${this.props.file_url}`
          }
          onLoadSuccess={this.onDocumentLoadSuccess}
          inputRef={this.inputRef}
        >
          {this.state.numPages.map((value, index) => {
            return <Page key={index} pageNumber={value} />;
          })}
        </Document>
      }

      {this.state.file !== '' ||
      this.state.scroll.scrollTop + this.state.scroll.offsetHeight <
        this.state.scroll.scrollHeight ? null : this.props.type === 'inet' ? (
        <Checkbox checked={this.state.checked} onChange={this.handleCheck}>
          Az MMSZ munkavállalóira vonatkozó adatvédelmi tájékoztatóban foglaltakat elolvastam,
          megértettem és tudomásul vettem.
        </Checkbox>
      ) : (
        <Checkbox checked={this.state.checked} onChange={this.handleCheck}>
          A konzorciumi partnerek munkavállalóira vonatkozó adatvédelmi tájékoztatóban foglaltakat
          elolvastam, megértettem és tudomásul vettem.
        </Checkbox>
      )}
    </Modal>*/
  );

  const renderCurrentStepContent = (): JSX.Element => {
    console.error(authToken?.terms_of_use);
    if (!firstToken) {
      return firstLoginStepContent;
    } else if (
      authToken?.terms_of_use !== undefined &&
      authToken?.terms_of_use !== null &&
      finishedConfirmation
    ) {
      dispatch(
        Presentation.actions.setShowModal(
          <TermsOfUseModalContent
            id={authToken?.terms_of_use.id}
            onClose={() => {
              dispatch(Auth.actions.setFirstToken(undefined));
              dispatch(Auth.actions.setAuthToken(undefined));
              dispatch(Presentation.actions.setShowModal(undefined));
              history.push(Paths.dashboard);
            }}
          />,
          () => {
            dispatch(Auth.actions.setFirstToken(undefined));
            dispatch(Auth.actions.setAuthToken(undefined));
            dispatch(Presentation.actions.setShowModal(undefined));
            history.push(Paths.dashboard);
          },
        ),
      );
      return termsOfUseStepContent;
    } else if (authToken?.need_password_update && finishedConfirmation) {
      return passwordChangeStepContent;
    } else {
      return secondLoginStepContent;
    }
  };

  return (
    <>
      <div className="login-background" />
      <div className="login-panel bg" />
      <div className="login-background-filter" />
      <div className="login-panel">
        <div className="login-panel-inner">
          <div className="flex flex-col w-1/1 mb-6">
            <div className="flex flex-row">
              <img
                className="login-dnet-icon"
                src={assets.icons.tradebull_logo_large}
                alt="TradeBull logo"
              />
              <p className="login-instructions" style={{ margin: 10 }}>
                {config.version}
              </p>
            </div>

            {renderCurrentStepContent()}
          </div>
          <LegalInformation />
        </div>
      </div>
      <CookieBar />
    </>
  );
};

export default withErrorBoundary(Login, { fallback: Fallback });
